import { Component, inject, OnInit } from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import {
    FormsModule,
    ReactiveFormsModule,
    FormBuilder,
    FormGroup,
    Validators,
    ValidatorFn,
    AbstractControl,
    FormControl,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatListModule } from '@angular/material/list';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { Router, RouterLink } from '@angular/router';
import { CustomizerSettingsService } from '../../customizer-settings/customizer-settings.service';
import { TranslocoModule } from '@ngneat/transloco';
import { ApiService } from '../../services/api/api.service';
import { Company } from '../../interfaces/Company';
import { CompanyResponse } from '../../interfaces/CompanyResponse';
import { BranchOffice } from './../../interfaces/BranchOffice';
import { BranchOfficeResponse } from './../../interfaces/BranchOfficeResponse';
import { CompanyUser } from '../../interfaces/CompanyUser';
import { Severity } from '../../interfaces/Severity';
import { SeverityResponse } from '../../interfaces/SeverityResponse';
import {
    FileUploadModule,
    FileUploadValidators,
} from '@iplab/ngx-file-upload';
import { VulnerabilityTool } from '../../interfaces/VulnerabilityTool';
import { VulnerabilityToolResponse } from '../../interfaces/VulnerabilityToolResponse';
import { CompanyUserResponse } from '../../interfaces/CompanyUserResponse';
import { Subscription } from 'rxjs';
import { VulnerabilityOrigin } from '../../interfaces/VulnerabilityOrigin';
import { VulnerabilityOriginResponse } from '../../interfaces/VulnerabilityOriginResponse';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AlertDialogComponent } from '../../common/alert-dialog/alert-dialog.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
    selector: 'app-v-create-assessment',
    standalone: true,
    imports: [
        MatTableModule,
        MatCardModule,
        MatMenuModule,
        MatButtonModule,
        RouterLink,
        FormsModule,
        FileUploadModule,
        MatFormFieldModule,
        MatInputModule,
        MatCheckboxModule,
        MatSelectModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatListModule,
        ReactiveFormsModule,
        MatProgressSpinnerModule,
        TranslocoModule,
        NgFor,
        NgIf,
    ],
    templateUrl: './v-create-assessment.component.html',
    styleUrl: './v-create-assessment.component.scss',
})
export class VCreateAssessmentComponent implements OnInit {
    // isToggled
    isToggled = false;

    // addAssessmentForm
    addAssessmentForm: FormGroup;

    // isLoadingSubmit
    isLoadingSubmit = false;

    // Subscription
    private subscription: Subscription;

    // Array of severities
    severities: Severity[] = [];

    selectedSeverity: Severity | undefined;

    // Array of vulnerability tools
    vulnerabilityTools: VulnerabilityTool[] = [];

    selectedVulnerabilityTool: VulnerabilityTool | undefined;

    // Array of vulnerability origin
    vulnerabilityOrigins: VulnerabilityOrigin[] = [];

    selectedVulnerabilityOrigin: VulnerabilityOrigin | undefined;

    // Array of companies
    companies: Company[] = [];

    selectedCompany: string = '';

    // Array of branch offices
    branchOffices: BranchOffice[] = [];

    selectedBranchOffice: BranchOffice | undefined;

    // Array of company users
    companyUsersList: CompanyUser[] = [];

    selectedUsers: string[] = [];

    // Start date
    startDate: Date | null = null;

    //End Date
    endDate: Date | null = null;

    // Show alert
    showAlert: boolean = false;

    fileUploadControl = new FormControl<File[]>([], [
        FileUploadValidators.filesLimit(1),
        FileUploadValidators.accept(['.zip', '.csv']),
    ]);

    readonly dialog = inject(MatDialog);

    constructor(
        private fb: FormBuilder,
        public themeService: CustomizerSettingsService,
        private apiService: ApiService,
        private router: Router
    ) {
        this.addAssessmentForm = this.fb.group(
            {
                assessmentName: [
                    '',
                    [Validators.required, Validators.maxLength(100)],
                ],
                description: ['', [Validators.maxLength(300)]],
                startDate: [
                    { value: this.startDate, disabled: true },
                    [Validators.required],
                ],
                endDate: [{ value: this.endDate, disabled: true }, []],
                branchOffice: [
                    this.selectedBranchOffice,
                    [Validators.required],
                ],
                severity: [this.selectedSeverity, [Validators.required]],
                vulnerabilityOrigin: [
                    this.selectedVulnerabilityOrigin,
                    [Validators.required],
                ],
                vulnerabilityTool: [this.selectedVulnerabilityTool, []],
                file: this.fileUploadControl,
                companyUsers: [this.selectedUsers, [Validators.required]],
            },
            {
                validator: this.fileValidator('vulnerabilityTool', 'file'),
            }
        );
        this.themeService.isToggled$.subscribe((isToggled) => {
            this.isToggled = isToggled;
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    ngOnInit() {
        this.apiService
            .getSeverity()
            .subscribe((response: SeverityResponse) => {
                this.severities.push(...response.severities);
            });
        this.apiService
            .getVulnerabilityOrigin()
            .subscribe((response: VulnerabilityOriginResponse) => {
                this.vulnerabilityOrigins.push(
                    ...response.vulnerabilityOrigins
                );
            });
        this.apiService
            .getVulnerabilityTool()
            .subscribe((response: VulnerabilityToolResponse) => {
                this.vulnerabilityTools.push(...response.vulnerabilityTools);
            });
        this.apiService
            .getCompanyPagination(0, 1000000, 'name', 'asc')
            .subscribe((response: CompanyResponse) => {
                this.companies.push(...response.companies);
            });
        this.subscription = this.addAssessmentForm.controls[
            'file'
        ].valueChanges.subscribe((values: Array<File>) =>
            this.changeFiles(values)
        );
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    // RTL Mode
    toggleRTLEnabledTheme() {
        this.themeService.toggleRTLEnabledTheme();
    }

    // Popup Trigger
    classApplied = false;
    toggleClass() {
        this.classApplied = !this.classApplied;
    }

    // Change company
    changeCompany(companyId: string) {
        this.branchOffices = [];
        this.companyUsersList = [];
        this.apiService
            .getBranchOfficeByCompanyId(companyId, 0, 1000000, 'name', 'asc')
            .subscribe((response: BranchOfficeResponse) => {
                if (response.branchOffices.length > 0) {
                    this.branchOffices.push(...response.branchOffices);
                }
            });
    }

    // Change branch office
    changeBranchOffice() {
        this.companyUsersList = [];
        if (this.selectedBranchOffice) {
            this.apiService
                .getCompanyUserByBranchOfficeId(
                    this.selectedBranchOffice.id,
                    0,
                    1000000,
                    'name',
                    'asc'
                )
                .subscribe((response: CompanyUserResponse) => {
                    if (response.companyUsers.length > 0) {
                        this.companyUsersList.push(...response.companyUsers);
                    }
                });
        }
    }

    // Change files
    private changeFiles(files: Array<File>): void {
        console.log('Files changed:', files);
        if (files && files[0]) {
            this.addAssessmentForm.controls['vulnerabilityTool'].addValidators(
                Validators.required
            );
            this.addAssessmentForm.updateValueAndValidity();
        } else {
            this.addAssessmentForm.controls[
                'vulnerabilityTool'
            ].removeValidators(Validators.required);
            this.addAssessmentForm.updateValueAndValidity();
        }
    }

    /**
     * On submit
     */
    onSubmit(): void {
        // Return if the form is invalid
        if (this.addAssessmentForm.invalid || this.classApplied) {
            this.showAlert = false;
            return;
        }

        // Disable the form
        this.addAssessmentForm.disable();

        // Show the spinner
        this.isLoadingSubmit = true;

        // Hide the alert
        this.showAlert = false;

        // Add Assesment
        const formData = new FormData();
        formData.append(
            'assessmentName',
            this.addAssessmentForm.get('assessmentName')?.value
        );
        formData.append(
            'description',
            this.addAssessmentForm.get('description')?.value
        );
        formData.append(
            'startDate',
            this.addAssessmentForm.get('startDate')?.value.getTime()
        );
        if (this.addAssessmentForm.get('endDate')?.value) {
            formData.append(
                'endDate',
                this.addAssessmentForm.get('endDate')?.value.getTime()
            );
        }
        formData.append(
            'companyUsers',
            this.addAssessmentForm.get('companyUsers')?.value
        );
        formData.append(
            'vulnerabilityOrigin.id',
            this.addAssessmentForm.get('vulnerabilityOrigin')?.value.id
        );
        if (this.selectedBranchOffice != undefined) {
            formData.append('branchOffice.id', this.selectedBranchOffice.id);
        }
        formData.append(
            'severity.id',
            this.addAssessmentForm.get('severity')?.value.id
        );
        if (this.selectedVulnerabilityTool != undefined) {
            formData.append(
                'vulnerabilityTool.id',
                this.selectedVulnerabilityTool.id
            );
        }
        if (this.fileUploadControl.value && this.fileUploadControl.value.length > 0) {
            formData.append(
                'file',
                this.fileUploadControl.value[0]
            );
        }

        this.apiService.addVulnerabilityAssessment(formData).subscribe({
            next: (value) => {
                this.fileUploadControl.setValue([]);
                this.addAssessmentForm.reset();
                // Re-enable the form
                this.addAssessmentForm.enable();

                // Show the alert
                this.showAlert = true;

                // Hide the spinner
                this.isLoadingSubmit = false;
            },
            error: (error) => {
                let errMsg: string = error;
                const dialogRef = this.dialog.open(AlertDialogComponent, {
                    data: {
                        title: 'ERROR',
                        message: errMsg,
                    },
                });
            },
            complete: () => {
                this.router.navigateByUrl('/vulnerability/assessments');
            },
        });
    }

    fileValidator(controlName: string, fileControlName: string): ValidatorFn {
        return (abstractControl: AbstractControl) => {
            const control = abstractControl.get(controlName);
            const fileControl = abstractControl.get(fileControlName);
            const file = this.fileUploadControl.value && this.fileUploadControl.value.length > 0 ? this.fileUploadControl.value[0] : null;

            if (this.selectedVulnerabilityTool != undefined && !file) {
                const error = {
                    requiredValidator: fileControlName + ' is required.',
                };
                fileControl!.setErrors(error);
                return error;
            } else if (this.selectedVulnerabilityTool == undefined && file) {
                const error = {
                    requiredValidator: controlName + ' is required.',
                };
                control!.setErrors(error);
                return error;
            } else {
                fileControl!.setErrors(null);
                return null;
            }
        };
    }
}
